import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { fmt } from "@/lib/utils-msip";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UploadCloud, CheckCircle2, AlertTriangle } from "lucide-react";
import { HeldUploads, SourceBadge, StatusBadge, fmtDay } from "@/components/HeldUploads";

export default function Uploads() {
  const { refreshMeta } = useApp();
  const [file, setFile] = useState(null);
  const [backfill, setBackfill] = useState(false);
  const [fy, setFy] = useState("");
  const [parsing, setParsing] = useState(false);
  const [review, setReview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [fileKey, setFileKey] = useState(0);
  const resetFile = () => { setFile(null); setFileKey((k) => k + 1); };
  const [history, setHistory] = useState([]);
  const [hrFile, setHrFile] = useState(null);
  const [hrUploading, setHrUploading] = useState(false);
  const [hrHistory, setHrHistory] = useState([]);

  const loadHistory = useCallback(() => {
    api.get("/uploads").then(({ data }) => setHistory(data)).catch(() => {});
    api.get("/hr/uploads").then(({ data }) => setHrHistory(data)).catch(() => {});
  }, []);
  useEffect(loadHistory, [loadHistory]);

  const uploadHr = async () => {
    if (!hrFile) return;
    setHrUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", hrFile);
      const { data } = await api.post("/hr/upload", fd);
      toast.success(`Manpower loaded: ${data.branches} branches, ${data.rows} rows`);
      setHrFile(null);
      loadHistory();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setHrUploading(false);
    }
  };

  const parse = async () => {
    if (!file) return;
    setParsing(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("backfill", backfill);
      if (fy) fd.append("fy_start", fy);
      const { data } = await api.post("/uploads/parse", fd);
      setReview(data);
      toast.success(`Parsed ${data.sheets.length} sheet(s) — review before committing`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setParsing(false);
    }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const { data } = await api.post(`/uploads/${review.upload_id}/commit`);
      setResult(data);
      setReview(null);
      resetFile();
      toast.success(`Committed: ${data.inserted} inserted, ${data.replaced} replaced`);
      refreshMeta();
      loadHistory();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCommitting(false);
    }
  };

  const discard = async () => {
    await api.post(`/uploads/${review.upload_id}/discard`).catch(() => {});
    setReview(null);
    resetFile();
    loadHistory();
  };

  return (
    <div className="space-y-5 max-w-5xl" data-testid="uploads-page">
      <h1 className="font-heading text-2xl font-extrabold tracking-tight">Uploads</h1>

      <div className="border bg-card rounded-md p-5 space-y-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1.5">Daily workbook (.xlsx)</label>
            <input
              key={fileKey}
              type="file"
              accept=".xlsx"
              data-testid="upload-file-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-sm file:mr-3 file:rounded-md file:border file:bg-secondary file:px-3 file:py-1.5 file:text-xs file:font-semibold file:border-border cursor-pointer"
            />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer pb-1.5">
            <Checkbox checked={backfill} onCheckedChange={setBackfill} data-testid="backfill-checkbox" />
            Backfill mode <span className="text-xs text-muted-foreground">(parse ALL date sheets — for historical load)</span>
          </label>
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1.5">Fiscal year</label>
            <Select value={fy || "auto"} onValueChange={(v) => setFy(v === "auto" ? "" : v)}>
              <SelectTrigger className="h-9 w-36 text-sm" data-testid="fy-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto-detect</SelectItem>
                {["2024", "2025", "2026", "2027"].map((y) => (
                  <SelectItem key={y} value={y}>FY {y}-{String(Number(y) + 1).slice(2)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={parse} disabled={!file || parsing} data-testid="parse-btn">
            <UploadCloud size={15} className="mr-1.5" /> {parsing ? "Parsing…" : "Parse & Review"}
          </Button>
        </div>
      </div>

      <div className="border bg-card rounded-md p-5 space-y-3" data-testid="hr-upload-card">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1.5">HR Manpower sheet (.xlsx)</label>
            <input
              type="file"
              accept=".xlsx"
              data-testid="hr-file-input"
              onChange={(e) => setHrFile(e.target.files?.[0] || null)}
              className="text-sm file:mr-3 file:rounded-md file:border file:bg-secondary file:px-3 file:py-1.5 file:text-xs file:font-semibold file:border-border cursor-pointer"
            />
          </div>
          <Button onClick={uploadHr} disabled={!hrFile || hrUploading} variant="outline" data-testid="hr-upload-btn">
            <UploadCloud size={15} className="mr-1.5" /> {hrUploading ? "Uploading…" : "Upload manpower"}
          </Button>
          {hrHistory[0] && (
            <span className="text-xs text-muted-foreground pb-2">
              Last: <b>{hrHistory[0].file_name}</b> · {hrHistory[0].timestamp?.slice(0, 16).replace("T", " ")} UTC · {hrHistory[0].branches} branches / {hrHistory[0].rows} rows
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground">One sheet per branch (Department / Designation / Required / Current / Gap) — re-upload anytime to refresh the Branch Report Cards.</p>
      </div>

      <HeldUploads items={history} excludeId={review?.upload_id} onDone={(what) => { if (what === "commit") refreshMeta(); loadHistory(); }} />

      {review && (
        <div className="border bg-card rounded-md p-5 space-y-4" data-testid="review-panel">
          <div className="flex items-center gap-2">
            <h3 className="font-heading font-semibold">Review — {review.file_name} (FY {review.fy_start}-{String(review.fy_start + 1).slice(2)})</h3>
            <span className="text-xs text-muted-foreground">{review.backfill ? "Backfill mode: all sheets" : "Newest MTD + YTD only"}</span>
          </div>
          <div className="overflow-auto max-h-64 border rounded-md">
            <table className="w-full text-xs">
              <thead className="border-b sticky top-0 bg-card">
                <tr>{["Sheet", "Scope", "Date (confirm)", "Rows", "Kerala total", "File KERALA", "Others %", "Warnings"].map((h) => <th key={h} className="px-2 py-1.5 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{h}</th>)}</tr>
              </thead>
              <tbody>
                {review.sheets.map((s) => (
                  <tr key={s.name} className="border-b last:border-0">
                    <td className="px-2 py-1.5 font-medium">{s.name}</td>
                    <td className="px-2 py-1.5">{s.scope}</td>
                    <td className="px-2 py-1.5 num font-semibold">{s.date}</td>
                    <td className="px-2 py-1.5 num">{s.rows}</td>
                    <td className="px-2 py-1.5 num">{fmt(s.kerala_total)}</td>
                    <td className="px-2 py-1.5 num">{fmt(s.kerala_file_total)}</td>
                    <td className="px-2 py-1.5 num">{s.others_pct}%</td>
                    <td className="px-2 py-1.5">{s.warnings.length > 0 ? <span className="text-amber-700">{s.warnings.length}</span> : "0"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap gap-4 text-xs">
            {review.skipped_sheets.length > 0 && (
              <div data-testid="skipped-sheets">
                <b>Skipped sheets:</b> {review.skipped_sheets.map((s) => <span key={s} className="inline-block ml-1 rounded bg-secondary px-1.5 py-0.5">{s}</span>)}
              </div>
            )}
            {review.new_rtos.length > 0 && (
              <div data-testid="new-rtos"><b>New RTOs ({review.new_rtos.length}):</b> {review.new_rtos.slice(0, 15).join(", ")}{review.new_rtos.length > 15 ? "…" : ""}</div>
            )}
          </div>
          {review.duplicate_dates.length > 0 && (
            <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900" data-testid="duplicate-warning">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span><b>Dates already in database (will be overwritten):</b> {review.duplicate_dates.join(", ")}</span>
            </div>
          )}
          {review.hold_reasons?.length > 0 && (
            <div className="rounded-md border p-3 text-xs space-y-1" data-testid="review-hold-reasons">
              <b>Would be held if pushed by the morning API:</b>
              {review.hold_reasons.map((r, i) => <div key={i} className="text-amber-800">• {r}</div>)}
            </div>
          )}
          {review.warnings.length > 0 && (
            <div className="rounded-md border p-3 max-h-36 overflow-auto text-xs space-y-1" data-testid="warnings-list">
              {review.warnings.map((w, i) => <div key={i} className="text-amber-800">⚠ {w}</div>)}
            </div>
          )}
          <div className="flex gap-2">
            <Button onClick={commit} disabled={committing} data-testid="commit-btn">
              <CheckCircle2 size={15} className="mr-1.5" /> {committing ? "Committing…" : "Confirm & Commit"}
            </Button>
            <Button variant="outline" onClick={discard} data-testid="discard-btn">Discard</Button>
          </div>
        </div>
      )}

      {result && (
        <div className="border border-emerald-200 bg-emerald-50 rounded-md p-4 text-sm text-emerald-900" data-testid="commit-result">
          <b>Committed.</b> {result.dates_ingested.length} sheet-dates ingested · {result.inserted} rows inserted · {result.replaced} replaced.
          {result.delta_warnings.length > 0 && <div className="mt-1 text-xs">Negative delta warnings: {result.delta_warnings.slice(0, 5).join("; ")}</div>}
          {result.cross_validation.length > 0 && <div className="mt-1 text-xs">{result.cross_validation.join("; ")}</div>}
        </div>
      )}

      <div className="border bg-card rounded-md overflow-auto max-h-[50vh]" data-testid="upload-history">
        <div className="p-4 pb-2"><h3 className="font-heading text-sm font-semibold">Upload history <span className="text-muted-foreground font-normal text-xs">· manual uploads and API pushes in one list</span></h3></div>
        <table className="w-full text-xs">
          <thead className="border-b">
            <tr>{["File", "Source", "By", "When", "As on", "Rows", "Status", "Warnings"].map((h) => <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{h}</th>)}</tr>
          </thead>
          <tbody>
            {history.map((h) => (
              <tr key={h.id} className="border-b last:border-0 hover:bg-muted/50" data-testid={`upload-row-${h.id}`}>
                <td className="px-3 py-1.5 font-medium">{h.file_name}{h.error && <div className="text-[10px] font-normal text-red-700 max-w-xs truncate" title={h.error}>{h.error}</div>}{h.status === "review" && h.hold_reasons?.length > 0 && <div className="text-[10px] font-normal text-amber-800 max-w-xs truncate" title={h.hold_reasons.join("; ")}>{h.hold_reasons[0]}</div>}</td>
                <td className="px-3 py-1.5"><SourceBadge h={h} /></td>
                <td className="px-3 py-1.5 text-muted-foreground">{h.uploaded_by}</td>
                <td className="px-3 py-1.5 num whitespace-nowrap">{h.timestamp?.slice(0, 16).replace("T", " ")}</td>
                <td className="px-3 py-1.5 num whitespace-nowrap">{h.as_on ? fmtDay(h.as_on) : "—"}</td>
                <td className="px-3 py-1.5 num">{h.rows_parsed}</td>
                <td className="px-3 py-1.5"><StatusBadge status={h.status} testId={`upload-status-${h.id}`} /></td>
                <td className="px-3 py-1.5 num">{h.warnings?.length || 0}</td>
              </tr>
            ))}
            {history.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">No uploads yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
